import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import Paper from 'widgets/Paper';
import Button from 'widgets/Button';
import Icon from 'components/Icon';
import colors from 'assets/styles/jsVariables/colors';
import SideLabel from './SideLabel';
import TitleAndLabel from './TitleAndLabel';

import './EditableLabel.scss';

const styles = {
  updateButtonStyle: {
    '&:hover': {
      background: `${colors.darkerCyan} !important`,
      boxShadow: 'rgba(176, 176, 176, 0.5) 0px 2px 5px 1px'
    },
    width: '165px',
    height: '45px',
    fontFamily: 'museosansrounded_500',
    '@media (max-width: 574px)': {
      width: '126px',
      padding: '8px 10px'
    }
  },
  updateButtonLabelStyle: {
    textTransform: 'none',
    fontSize: '16px',
    letterSpacing: '0.3px'
  },
  cancelButtonStyle: {
    '&:hover': {
      background: '#a8a8a8 !important',
      boxShadow: 'rgba(176, 176, 176, 0.5) 0px 2px 5px 1px'
    },
    width: '90px',
    height: '45px',
    marginLeft: '10px',
    fontFamily: 'museosansrounded_500',
    '@media (max-width: 574px)': {
      padding: '8px 10px'
    }
  },
  cancelButtonLabelStyle: {
    textTransform: 'none',
    fontSize: '16px',
    letterSpacing: '0.3px'
  },
  paperStyleRoot: {
    borderRadius: '0px',
    backgroundColor: 'white',
    position: 'relative',
    outline: 'none',
    boxShadow: 'none'
  }
};

class EditableLabel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editMode: false,
      value: props.value || '',
      validationMsg: ''
    };
    this.titleAndLabel = React.createRef();
  }

  setEditMode = () => {
    const { clearValueOnEdit, value } = this.props;
    this.setState({
      editMode: true,
      value: clearValueOnEdit ? '' : value
    }, () => {
      this.titleAndLabel.current.focus();
      this.titleAndLabel.current.select();
    });
  };

  setLabelMode = () => {
    this.setState({
      editMode: false,
      validationMsg: ''
    });
    if (window.getSelection) {
      window.getSelection().removeAllRanges();
    }
  }

  handleChange = (value) => {
    const { validation, intl } = this.props;
    const validationMsg = validation(value, intl);
    this.setState({ validationMsg, value });
  };

  checkSaveChangesClickable() {
    const { value, validationMsg } = this.state;
    if (!validationMsg && value && (value !== this.props.value)) {
      return true;
    }
    return false;
  }

  handleSaveChangesClick = () => {
    const { value } = this.state;
    if (this.checkSaveChangesClickable()) {
      this.props.onSaveChanges(value);
      this.setLabelMode();
    }
  }

  renderTitleAndLabel = () => {
    const {
      title,
      value: displayValue,
      labelStyle,
      disabled,
      tabIndex
    } = this.props;
    const {
      editMode,
      value,
      validationMsg
    } = this.state;
    return (
      <TitleAndLabel
        editMode={editMode}
        tabIndex={tabIndex}
        value={value}
        ref={this.titleAndLabel}
        handleChange={this.handleChange}
        onEnter={this.handleSaveChangesClick}
        displayValue={displayValue}
        labelStyle={labelStyle}
        title={title}
        disabled={disabled}
        validationMsg={validationMsg}
      />
    );
  }

  renderEditLabel = () => {
    const {
      editLabel,
      disabled,
      iconSize,
      windowDimension
    } = this.props;
    return (
      <SideLabel
        disabled={disabled}
        iconSize={iconSize}
        windowDimension={windowDimension}
        editLabel={editLabel}
        onClick={this.setEditMode}
      />
    );
  }

  render() {
    const {
      className,
      tabIndex,
      disabled,
      style,
      isSocialLogin,
      noteText,
      saveButtonLabel,
      cancelButtonLabel,
      noteHeading
    } = this.props;
    const { editMode, validationMsg } = this.state;
    const isSaveChangesClickable = this.checkSaveChangesClickable();
    return (
      <div>
        <Paper
          className={className}
          cssStyles={{
            root: styles.paperStyleRoot
          }}
          id={`containerEditableLabel-${tabIndex}`}
          tabIndex={disabled ? null : tabIndex}
          style={{
            ...style
          }}
        >
          {
            (!editMode && !isSocialLogin) &&
            <div className="EditableLabel__RightContainer">
              {this.renderEditLabel()}
            </div>
          }
          <div className="EditableLabel__LeftContainer">
            {
              this.renderTitleAndLabel()
            }
            {
              !!validationMsg &&
              <div className="EditableLabel__LeftContainer__ValidationMsg" >
                {validationMsg}
              </div>
            }
          </div>
          {
            editMode &&
            <div>
              <div className="EditableLabel__Note__Container">
                <Icon
                  name="warning"
                  fill="#7d7d7d"
                />
                <div className="EditableLabel__Note__Text">
                  <span>{noteHeading}&nbsp;</span><span>{noteText}</span>
                </div>
              </div>
              <div>
                <Button
                  cssStyles={{
                    root: styles.updateButtonStyle,
                    label: styles.updateButtonLabelStyle
                  }}
                  style={{
                    backgroundColor: (disabled || !isSaveChangesClickable) ?
                      colors.disabledLighterCyan :
                      colors.lighterCyan
                  }}
                  disabled={disabled || !isSaveChangesClickable}
                  onClick={this.handleSaveChangesClick}
                >
                  {saveButtonLabel}
                </Button>
                <Button
                  cssStyles={{
                    root: styles.cancelButtonStyle,
                    label: styles.cancelButtonLabelStyle
                  }}
                  style={{
                    backgroundColor: '#c6c6c6'
                  }}
                  onClick={this.setLabelMode}
                >
                  {cancelButtonLabel}
                </Button>
              </div>
            </div>
          }
        </Paper>
      </div>
    );
  }
}

EditableLabel.propTypes = {
  tabIndex: PropTypes.string.isRequired,
  value: PropTypes.string,
  title: PropTypes.string.isRequired,
  editLabel: PropTypes.string.isRequired,
  iconSize: PropTypes.number,
  disabled: PropTypes.bool,
  clearValueOnEdit: PropTypes.bool,
  isSocialLogin: PropTypes.bool,
  style: PropTypes.object,
  windowDimension: PropTypes.object.isRequired,
  labelStyle: PropTypes.object,
  validation: PropTypes.func,
  onSaveChanges: PropTypes.func,
  className: PropTypes.string,
  noteText: PropTypes.string.isRequired,
  saveButtonLabel: PropTypes.string.isRequired,
  cancelButtonLabel: PropTypes.string.isRequired,
  noteHeading: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired
};
EditableLabel.defaultProps = {
  value: '',
  disabled: false,
  clearValueOnEdit: false,
  isSocialLogin: false,
  style: null,
  labelStyle: {},
  iconSize: 15,
  validation: () => (''),
  onSaveChanges: () => {},
  className: '',
};

export default connect(({ windowDimension }) =>
  ({ windowDimension }))(injectIntl(EditableLabel));



// WEBPACK FOOTER //
// ./src/components/EditableLabel/EditableLabel.jsx